/**
 * Cell types whose empty value is not null.
 *
 * Deliberately short: it covers the cells where null is actively wrong — a
 * boolean column is `not null` in the database, so a new row has to start false
 * rather than null. Everything else keeps falling through to `default`, which is
 * the long-standing behaviour.
 */
const CELL_PROCESSORS = {
    Boolean: 'boolean',
    Checkbox: 'boolean',
    EditableBoolean: 'boolean'
};

export class GridDataAdapter {
    static processorsMap = {
        object: (data, field) => {
            if (field.outputField) {
                return data?.[field.object]?.[field.outputField] || null;
            }
            if (field.fullObject) {
                return data?.[field.object] || null;
            }
            return data?.[field.object]?.id || null;
        },
        array: (data, field) => {
            const raw = data?.[field.array];
            return Array.isArray(raw) ? raw.map((item) => item.id) : [];
        },
        boolean: (data, field) => data?.[field.id] ?? false,
        number: (data, field) => data?.[field.id] ?? 0,
        default: (data, field) => data?.[field.id] ?? null,
        tags: (data, field) => data?.[field.id] ?? [],
    };

    static transform(data, fields) {
        const transformedData = {};

        if (data?.id) {
            transformedData.id = data.id;
        }

        fields
            .filter((f) => !f.hideOnAdd)
            .forEach((field) => {
                // Precedence, and the order matters: an explicit `type` wins,
                // then `object`, then the cell map, then `default`.
                //
                // `object` has to come before the cell map and be resolved here
                // rather than in a fallback. An earlier version of this computed
                // the key as `field.type || CELL_PROCESSORS[field.cell] ||
                // 'default'` and left `object` to a `||` fallback on the lookup —
                // but 'default' always resolves to a real processor, so the
                // fallback was dead code and every object-backed field (which is
                // every Select) read data[field.id] instead of
                // data[field.object].id. Editing a row showed its text fields and
                // empty selectors.
                //
                // The cell map exists because head configs are written with
                // `cell` and only three fields in the app set `type`, so the
                // boolean processor almost never ran and booleans fell through to
                // `default`, which yields null — that is how a new merchant came
                // to post isArchived: null. It is consulted only for fields with
                // no `object`, so it cannot disturb the Select path.
                const processorKey =
                    field.type
                    || (field.object ? 'object' : null)
                    || CELL_PROCESSORS[field.cell]
                    || 'default';
                const processor =
                    this.processorsMap[processorKey] || this.processorsMap.default;
                transformedData[field.id] = processor(data, field);
            });
        return transformedData;
    }
}
